// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState } from 'react';

import type { GatewayStatus, GatewayViewProps, LfGateway } from '../../../types';
import { gatewayAPI } from '../../../utils/apiClient';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { useWarnings } from '../../../utils/hooks';
import logger from '../../../utils/logger';
import { WARNING_TYPE_LABELS } from '../../../utils/maintenanceUtils';
import { Button } from '../../common/Button';

import { ReplaceDeviceModal } from './ReplaceDeviceModal';

interface GatewayUpdates {
  gateway_eui?: string;
  lora_server?: string;
  deck?: number;
  side?: string;
  frame_number?: number | null;
}

interface StatusStyle {
  icon: string;
  label: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

interface GatewayStats {
  total: number;
  online: number;
  offline: number;
  neverSeen: number;
}

/**
 * GatewayView Component - LF Gateway management
 *
 * Displays LF Gateways with synced Chirpstack status:
 * - Online/offline status indicators
 * - Gateway configuration (gateway_eui, lora_server)
 * - Edit functionality for gateway metadata
 * - Warning indicators
 * - Device replacement support
 *
 * @example
 * <GatewayView
 *   gateways={gateways}
 *   onRefresh={fetchGateways}
 * />
 */
export const GatewayView: React.FC<GatewayViewProps> = ({ gateways, onRefresh }) => {
  const [selectedGateway, setSelectedGateway] = useState<LfGateway | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [replaceMode, setReplaceMode] = useState<boolean>(false);
  const [editedGatewayEui, setEditedGatewayEui] = useState<string>('');
  const [editedLoraServer, setEditedLoraServer] = useState<string>('');
  const [editedDeck, setEditedDeck] = useState<string>('0');
  const [editedSide, setEditedSide] = useState<string>('Unknown');
  const [editedFrameNumber, setEditedFrameNumber] = useState<string>('');

  // Use shared warnings hook
  const { warnings: activeWarnings, getWarningsForDevice } = useWarnings({
    category: 'lf_gateway',
  });

  /**
   * Get status styling based on Chirpstack status
   */
  const getStatusStyle = (status?: GatewayStatus): StatusStyle => {
    switch (status) {
      case 'online':
        return {
          icon: '🟢',
          label: 'ONLINE',
          bgColor: '#e8f5e9',
          borderColor: '#4CAF50',
          textColor: '#2e7d32',
        };
      case 'offline':
        return {
          icon: '🔴',
          label: 'OFFLINE',
          bgColor: '#ffebee',
          borderColor: '#f44336',
          textColor: '#c62828',
        };
      case 'never_seen':
      default:
        return {
          icon: '⚪',
          label: 'NEVER SEEN',
          bgColor: '#f5f5f5',
          borderColor: '#999',
          textColor: '#666',
        };
    }
  };

  /**
   * Get overall statistics
   */
  const getStats = (): GatewayStats => {
    let online = 0;
    let offline = 0;
    let neverSeen = 0;

    gateways.forEach((gw) => {
      if (gw.chirpstack_status === 'online') online++;
      else if (gw.chirpstack_status === 'offline') offline++;
      else neverSeen++;
    });

    return { total: gateways.length, online, offline, neverSeen };
  };

  /**
   * Handle gateway update
   */
  const handleUpdateGateway = async (gatewayId: number, updates: GatewayUpdates): Promise<void> => {
    try {
      const data = await gatewayAPI.update(gatewayId, updates);
      if (data.success) {
        logger.debug(`Gateway ${gatewayId} updated`);
        setEditMode(false);
        setSelectedGateway(null);
        if (onRefresh) onRefresh();
      } else {
        alert(`Failed to update gateway: ${data.error}`);
      }
    } catch (err) {
      console.error('Error updating gateway:', err);
      alert(`Failed to update gateway: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  /**
   * Open edit mode
   */
  const openEditMode = (gateway: LfGateway): void => {
    setEditMode(true);
    setEditedGatewayEui(gateway.gateway_eui || '');
    setEditedLoraServer(gateway.lora_server || '');
    setEditedDeck(String(gateway.deck ?? 0));
    setEditedSide(gateway.side || 'Unknown');
    setEditedFrameNumber(gateway.frame_number != null ? String(gateway.frame_number) : '');
  };

  /**
   * Save gateway edits
   */
  const saveGatewayEdit = (): void => {
    if (!selectedGateway) return;

    const updates: GatewayUpdates = {};
    if (editedGatewayEui !== (selectedGateway.gateway_eui || '')) {
      updates.gateway_eui = editedGatewayEui;
    }
    if (editedLoraServer !== (selectedGateway.lora_server || '')) {
      updates.lora_server = editedLoraServer;
    }
    const newDeck = parseInt(editedDeck) || 0;
    if (newDeck !== (selectedGateway.deck ?? 0)) {
      updates.deck = newDeck;
    }
    if (editedSide !== (selectedGateway.side || 'Unknown')) {
      updates.side = editedSide;
    }
    const newFrameNumber = editedFrameNumber !== '' ? parseInt(editedFrameNumber) : null;
    if (newFrameNumber !== (selectedGateway.frame_number ?? null)) {
      updates.frame_number = newFrameNumber;
    }

    if (Object.keys(updates).length > 0) {
      handleUpdateGateway(selectedGateway.id, updates);
    }
    setEditMode(false);
    setSelectedGateway(null);
  };

  /**
   * Handle device replacement completion
   */
  const handleDeviceReplacement = (): void => {
    setReplaceMode(false);
    setSelectedGateway(null);
    if (onRefresh) onRefresh();
  };

  const stats = getStats();

  if (gateways.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '4vmin',
          fontSize: '2vmin',
          color: '#666',
          background: 'white',
          borderRadius: '1vmin',
          border: '1px solid #ddd',
        }}
      >
        <div style={{ marginBottom: '2vmin', fontSize: '4vmin' }}>🌐</div>
        <div>No gateways found</div>
      </div>
    );
  }

  return (
    <div>
      {/* Overall statistics */}
      <div
        style={{
          background: '#f9f9f9',
          padding: '1.5vmin',
          borderRadius: '0.5vmin',
          marginBottom: '2vmin',
          display: 'flex',
          gap: '2vmin',
          fontSize: '1.8vmin',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong>📊 Total:</strong> {stats.total} gateways
        </div>
        <div style={{ color: '#2e7d32' }}>
          <strong>🟢 Online:</strong> {stats.online}
        </div>
        <div style={{ color: '#c62828' }}>
          <strong>🔴 Offline:</strong> {stats.offline}
        </div>
        {stats.neverSeen > 0 && (
          <div style={{ color: '#666' }}>
            <strong>⚪ Never seen:</strong> {stats.neverSeen}
          </div>
        )}
      </div>

      {/* Active warnings banner */}
      {activeWarnings.length > 0 && (
        <div
          style={{
            marginBottom: '2vmin',
            borderRadius: '0.5vmin',
            border: `2px solid ${activeWarnings.some((w) => w.severity === 'critical') ? '#f44336' : '#ff9800'}`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: activeWarnings.some((w) => w.severity === 'critical')
                ? '#f44336'
                : '#ff9800',
              color: 'white',
              padding: '0.8vmin 1.5vmin',
              fontSize: '1.6vmin',
              fontWeight: 'bold',
            }}
          >
            {activeWarnings.some((w) => w.severity === 'critical') ? '🔴' : '⚠️'} Active Warnings (
            {activeWarnings.length})
          </div>
          <div style={{ background: '#fffdf9', padding: '1vmin' }}>
            {activeWarnings.map((warning) => {
              const gateway = gateways.find((g) => g.device_id === warning.device);
              const isCritical = warning.severity === 'critical';
              return (
                <div
                  key={warning.id}
                  onClick={() => gateway && setSelectedGateway(gateway)}
                  style={{
                    fontSize: '1.5vmin',
                    padding: '0.8vmin 1vmin',
                    marginBottom: '0.5vmin',
                    background: isCritical ? '#ffebee' : '#fff8e1',
                    borderLeft: `4px solid ${isCritical ? '#f44336' : '#ff9800'}`,
                    borderRadius: '0.3vmin',
                    cursor: gateway ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1vmin',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      background: isCritical ? '#f44336' : '#ff9800',
                      color: 'white',
                      fontSize: '1.1vmin',
                      fontWeight: 'bold',
                      padding: '0.2vmin 0.6vmin',
                      borderRadius: '1vmin',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isCritical ? '🔴 Critical' : '⚠ Warning'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <strong>{gateway?.name ?? warning.device}</strong>
                    {' — '}
                    {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                  </span>
                  <span style={{ fontSize: '1.3vmin', color: '#888', whiteSpace: 'nowrap' }}>
                    {new Date(warning.detected_at).toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gateway list */}
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          border: '1px solid #ddd',
          overflow: 'hidden',
        }}
      >
        {gateways.map((gateway) => {
          const statusStyle = getStatusStyle(gateway.chirpstack_status);

          return (
            <div
              key={gateway.id}
              onClick={() => setSelectedGateway(gateway)}
              style={{
                padding: '1.5vmin',
                borderBottom: '1px solid #eee',
                background: statusStyle.bgColor,
                borderLeft: `5px solid ${statusStyle.borderColor}`,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 'max(13px, 1.7vmin)',
                transition: 'transform 0.1s, box-shadow 0.1s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(3px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1vmin' }}>
                <span style={{ fontSize: 'max(14px, 2vmin)' }}>{statusStyle.icon}</span>
                <div style={{ textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 'max(13px, 1.8vmin)' }}>
                    {gateway.name || `Gateway ${gateway.id}`}
                  </div>
                  <div
                    style={{ fontSize: 'max(11px, 1.4vmin)', color: '#666', marginTop: '0.3vmin' }}
                  >
                    {gateway.device_id}
                  </div>
                </div>
              </div>
              <div
                style={{
                  minWidth: '120px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: statusStyle.textColor,
                  fontSize: 'max(12px, 1.5vmin)',
                }}
              >
                {statusStyle.label}
              </div>
              <div
                style={{
                  minWidth: '150px',
                  textAlign: 'right',
                  color: '#666',
                  fontSize: 'max(12px, 1.5vmin)',
                }}
              >
                {formatRelativeTime(gateway.last_seen)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Gateway detail popup */}
      {selectedGateway && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            // flex-start + paddingTop instead of center: prevents modal from extending below the visible
            // viewport on iOS where 100vh includes browser chrome behind the address/tab bar
            alignItems: 'flex-start',
            paddingTop: 'max(20px, 5vh)',
            zIndex: 3000,
          }}
          onClick={() => {
            setEditMode(false);
            setSelectedGateway(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '1vmin',
              padding: '2vmin',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              // dvh (dynamic viewport height) excludes browser chrome — ensures modal fits visible area on iOS
              maxHeight: 'min(90vh, 85dvh)',
              overflowY: 'scroll',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2vmin',
                borderBottom: '2px solid #ddd',
                paddingBottom: '1vmin',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '2.5vmin' }}>
                {editMode
                  ? 'Edit Gateway'
                  : selectedGateway.name || `Gateway ${selectedGateway.id}`}
              </h2>
              <Button
                text="✕"
                onClick={() => {
                  setEditMode(false);
                  setSelectedGateway(null);
                }}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                }}
              />
            </div>

            {/* Details or Edit Form */}
            {editMode ? (
              <div style={{ fontSize: '1.8vmin' }}>
                <div style={{ marginBottom: '1.5vmin' }}>
                  <label style={{ display: 'block', marginBottom: '0.5vmin', fontWeight: 'bold' }}>
                    Gateway EUI:
                  </label>
                  <input
                    type="text"
                    value={editedGatewayEui}
                    onChange={(e) => setEditedGatewayEui(e.target.value)}
                    placeholder="e.g. aa11bb22cc33dd44"
                    style={{
                      width: '100%',
                      padding: '1vmin',
                      fontSize: '1.6vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                      fontFamily: 'monospace',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1.5vmin' }}>
                  <label style={{ display: 'block', marginBottom: '0.5vmin', fontWeight: 'bold' }}>
                    LoRa Server:
                  </label>
                  <input
                    type="text"
                    value={editedLoraServer}
                    onChange={(e) => setEditedLoraServer(e.target.value)}
                    placeholder="https://lora.example.com"
                    style={{
                      width: '100%',
                      padding: '1vmin',
                      fontSize: '1.6vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1.5vmin' }}>
                  <label style={{ display: 'block', marginBottom: '0.5vmin', fontWeight: 'bold' }}>
                    Frame No:
                  </label>
                  <input
                    type="number"
                    value={editedFrameNumber}
                    onChange={(e) => setEditedFrameNumber(e.target.value)}
                    placeholder="Frame number"
                    style={{
                      width: '100%',
                      padding: '1vmin',
                      fontSize: '1.6vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1vmin', marginBottom: '1.5vmin' }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{ display: 'block', marginBottom: '0.5vmin', fontWeight: 'bold' }}
                    >
                      Deck:
                    </label>
                    <input
                      type="number"
                      value={editedDeck}
                      onChange={(e) => setEditedDeck(e.target.value)}
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '1vmin',
                        fontSize: '1.6vmin',
                        border: '1px solid #ccc',
                        borderRadius: '0.5vmin',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{ display: 'block', marginBottom: '0.5vmin', fontWeight: 'bold' }}
                    >
                      Side:
                    </label>
                    <input
                      type="text"
                      value={editedSide}
                      onChange={(e) => setEditedSide(e.target.value)}
                      placeholder="e.g. Port, Starboard"
                      style={{
                        width: '100%',
                        padding: '1vmin',
                        fontSize: '1.6vmin',
                        border: '1px solid #ccc',
                        borderRadius: '0.5vmin',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Status badge */}
                {(() => {
                  const statusStyle = getStatusStyle(selectedGateway.chirpstack_status);
                  return (
                    <div
                      style={{
                        background: statusStyle.bgColor,
                        border: `2px solid ${statusStyle.borderColor}`,
                        borderRadius: '0.5vmin',
                        padding: '1vmin',
                        marginBottom: '2vmin',
                        textAlign: 'center',
                        fontSize: '2vmin',
                        fontWeight: 'bold',
                        color: statusStyle.textColor,
                      }}
                    >
                      {statusStyle.icon} {statusStyle.label}
                    </div>
                  );
                })()}

                {/* Details */}
                <div style={{ fontSize: '1.8vmin', lineHeight: '2.5' }}>
                  <div>
                    <strong>Gateway EUI:</strong>{' '}
                    <span style={{ fontFamily: 'monospace' }}>
                      {selectedGateway.gateway_eui || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <strong>LoRa Server:</strong> {selectedGateway.lora_server || 'N/A'}
                  </div>
                  <div>
                    <strong>Frame No:</strong> {selectedGateway.frame_number ?? 'N/A'}
                  </div>
                  <div>
                    <strong>Deck:</strong> {selectedGateway.deck ?? 0}
                  </div>
                  <div>
                    <strong>Side:</strong> {selectedGateway.side || 'Unknown'}
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #eee',
                      marginTop: '1vmin',
                      paddingTop: '1vmin',
                    }}
                  ></div>
                  <div>
                    <strong>Device ID:</strong> {selectedGateway.device_id}
                  </div>
                  <div>
                    <strong>Last seen:</strong> {formatRelativeTime(selectedGateway.last_seen)}
                  </div>
                </div>

                {/* Warnings from warning system */}
                {(() => {
                  const deviceWarnings = getWarningsForDevice(selectedGateway.device_id);
                  if (deviceWarnings.length > 0) {
                    return (
                      <div
                        style={{
                          marginTop: '2vmin',
                          padding: '1.5vmin',
                          background: '#fff3e0',
                          borderRadius: '0.5vmin',
                          border: '2px solid #ff9800',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '1.8vmin',
                            fontWeight: 'bold',
                            marginBottom: '1vmin',
                          }}
                        >
                          ⚠️ Active Warnings ({deviceWarnings.length})
                        </div>
                        {deviceWarnings.map((warning, idx) => {
                          return (
                            <div
                              key={idx}
                              style={{
                                fontSize: '1.5vmin',
                                padding: '0.8vmin',
                                marginBottom: '0.5vmin',
                                background: warning.severity === 'critical' ? '#ffebee' : '#fff',
                                borderLeft: `3px solid ${warning.severity === 'critical' ? '#f44' : '#ff9800'}`,
                                borderRadius: '0.3vmin',
                              }}
                            >
                              <div style={{ fontWeight: 'bold' }}>
                                {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                              </div>
                              <div style={{ fontSize: '1.3vmin', color: '#666' }}>
                                Detected: {new Date(warning.detected_at).toLocaleString()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}

            {/* Control buttons */}
            <div
              style={{
                marginTop: '2vmin',
                paddingTop: '2vmin',
                borderTop: '1px solid #ddd',
                display: 'flex',
                gap: '1vmin',
                flexWrap: 'wrap',
              }}
            >
              {editMode ? (
                <>
                  <Button
                    text="✓ Save"
                    onClick={saveGatewayEdit}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: '#4CAF50',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Button
                    text="Cancel"
                    onClick={() => setEditMode(false)}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: '#ccc',
                      color: '#333',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </>
              ) : (
                <div style={{ display: 'flex', gap: '0.5vmin', width: '100%' }}>
                  <Button
                    text="✏️ Edit"
                    onClick={() => openEditMode(selectedGateway)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: '#2196F3',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Button
                    text="🔄 Replace"
                    onClick={() => setReplaceMode(true)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: '#FF9800',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Replace device modal */}
      {replaceMode && selectedGateway && (
        <ReplaceDeviceModal
          device={selectedGateway}
          deviceCategory="lf_gateway"
          onReplace={handleDeviceReplacement}
          onCancel={() => setReplaceMode(false)}
        />
      )}
    </div>
  );
};

export default GatewayView;
